<template>
    <div
        v-on-click-outside="hidePalette"
        class="relative inline-block rounded-sm cursor-pointer"
        :class="squareSize"
        :style="{ backgroundColor: modelValue }"
        @click="togglePalette"
    >
        <div v-show="show" class="absolute right-0 z-10 top-full">
            <ul
                :dusk="dusk"
                class="grid grid-cols-8 gap-1 p-1 mt-1 text-right bg-white border border-gray-300 shadow dark:bg-zinc-800 dark:border-zinc-700"
                :class="`w-${paletteWidth}`"
            >
                <li
                    v-for="(color, colorindex) in colors"
                    :key="colorindex"
                    class="rounded-sm cursor-pointer hover:opacity-80"
                    :class="squareSize"
                    :style="{ backgroundColor: color.id }"
                    :title="color.name"
                    @click="setColor(color)"
                ></li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { watch, ref, computed } from 'vue';
import { colors } from '@src/Common/Helpers/colors';
import type { Color, Colors } from '@src/Common/DataGrid/types';
import { vOnClickOutside } from '@vueuse/components';
import { ComponentSize } from '@src/Common/DataGrid/types';

const emit = defineEmits<{
    (event: 'update:modelValue', content: string | null): void;
}>();

const show = ref(false);

interface Props {
    modelValue?: string | null;
    dusk?: string | null;
    colors?: Colors;
    size?: ComponentSize;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '#cccccc',
    dusk: null,
    colors: () => colors,
    size: 'base',
});

const modelValue = ref(props.modelValue);

watch(modelValue, (value: string) => {
    emit('update:modelValue', value);
});

const setColor = (color: Color) => {
    modelValue.value = color.id;
};

const hidePalette = () => {
    show.value = false;
};

const togglePalette = () => {
    show.value = !show.value;
};

const squareSize = computed((): string => {
    const sizes = {
        xs: 'w-4 h-4',
        sm: 'w-5 h-5',
        base: 'w-6 h-6',
        lg: 'w-8 h-8',
        xl: 'w-10 h-10',
    };

    return sizes[props.size] ?? sizes.base;
});

const paletteWidth = computed(() => {
    const sizes = {
        xs: 40,
        sm: 48,
        base: 60,
        lg: 80,
        xl: 100,
    };

    return sizes[props.size] ?? sizes.base;
});
</script>
